import type React from "react";
import {useMemo} from "react";
import {Box, Skeleton, Stack, Typography} from "@mui/material";
import type {EChartsType} from "echarts/types/dist/shared";
import {CustomCard} from "~/components/Card/Card";
import {MithraEchart} from "~/services/echarts/MithraEchart";
import type {EChartContextType} from "~/services/echarts/EChartProvider";
import {CurrencyComponent} from "~/components/currency-component/CurrencyComponent";
import {findAbbreviationOfGroup, toCurrency} from "~/components/currency-component/CurrencyClasses";
import {COLORS} from "~/style/colors-ts";
import {useStores} from "~/stores";
import {useSupplierSpendTrendQuery} from "~/pages/supplier-360/tabs/SupplierOverview/hooks/useSupplierSpendTrendQuery";
import type {SupplierSpendTrend} from "~/pages/supplier-360/types/Supplier360Types";

const CHART_HEIGHT = 260;

/** `2026-07` -> `Jul 26`. The endpoint returns ISO `YYYY-MM` and leaves month naming to us (§2.22). */
function monthLabel(iso: string): string {
    const [year, month] = iso.split('-');
    const date = new Date(Number(year), Number(month) - 1, 1);
    return `${date.toLocaleDateString('en-GB', {month: 'short'})} ${year.slice(2)}`;
}

function sum(values: number[]): number {
    return values.reduce((acc, v) => acc + v, 0);
}

function buildTrendOption(trend: SupplierSpendTrend, currencySymbol: string) {
    // One shared abbreviation across every tick and tooltip, so the axis doesn't mix k and M
    const abbreviation = findAbbreviationOfGroup([...trend.curr, ...trend.prev, 0]);
    const format = (v: number) => toCurrency(currencySymbol, v, 3, abbreviation);

    return {
        tooltip: {
            trigger: 'axis',
            valueFormatter: (v: number) => format(Number(v)),
        },
        grid: {top: 8, left: 4, right: 8, bottom: 0, containLabel: true},
        xAxis: {
            type: 'category',
            boundaryGap: false,
            data: trend.months.map(monthLabel),
            axisTick: {show: false},
            axisLabel: {fontSize: 10.5, color: COLORS.grey[500], hideOverlap: true},
            axisLine: false,
        },
        yAxis: {
            type: 'value',
            axisTick: {show: false},
            axisLine: false,
            axisLabel: {formatter: format, fontSize: 10.5, color: COLORS.grey[500]},
            splitLine: {show: true, lineStyle: {color: COLORS.grey[150], width: 2}},
        },
        series: [
            {
                name: trend.curr_label,
                type: 'line',
                smooth: false,
                symbol: 'emptyCircle',
                symbolSize: 6,
                data: trend.curr,
                itemStyle: {color: COLORS.info.main},
                lineStyle: {width: 2.2, color: COLORS.info.main},
                areaStyle: {color: COLORS.info.main, opacity: 0.08},
                z: 3,
            },
            {
                name: trend.prev_label,
                type: 'line',
                smooth: false,
                symbol: 'none',
                data: trend.prev,
                itemStyle: {color: COLORS.grey[400]},
                lineStyle: {width: 1.6, type: 'dashed', color: COLORS.grey[400]},
                z: 2,
            },
        ],
    };
}

function initTrendChart(
    echart: EChartContextType,
    root: HTMLElement,
    trend: SupplierSpendTrend,
    currencySymbol: string,
): EChartsType {
    const chart = echart.init(root, 'mithra', {renderer: 'svg'});
    chart.setOption(buildTrendOption(trend, currencySymbol));

    const container = root.parentElement ?? root;
    let frame = 0;
    const observer = new ResizeObserver(() => {
        if (!container.isConnected) {
            cancelAnimationFrame(frame);
            observer.disconnect();
            return;
        }
        cancelAnimationFrame(frame);
        frame = requestAnimationFrame(() => {
            if (chart.isDisposed()) return;
            chart.resize({width: container.clientWidth, height: container.clientHeight});
        });
    });
    observer.observe(container);

    const dispose = chart.dispose.bind(chart);
    chart.dispose = () => {
        cancelAnimationFrame(frame);
        observer.disconnect();
        dispose();
    };

    return chart;
}

/** Overview tab "Spend trend" — monthly spend, last 12 months vs prior year (§2.22, §3.3). */
export const SpendTrendSection: React.FC<{entityId: number}> = ({entityId}) => {
    const {p} = useStores();
    const {data: trend, isLoading} = useSupplierSpendTrendQuery(entityId);

    const chartData = useMemo(() => trend ? [trend] : [], [trend]);

    // Only the two legend totals are ours to derive; the YoY badge reads the endpoint's own
    // `yoy_pct`, which is a calendar year-to-date comparison and not derivable from these sums.
    const totals = useMemo(() => {
        if (!trend) return null;
        return {curr: sum(trend.curr), prev: sum(trend.prev)};
    }, [trend]);

    const yoyPct = trend?.yoy_pct ?? null;
    return (
        <CustomCard
            title="Spend trend"
            extraHeaderContent={<Typography variant="caption">Last 12 months vs prior year</Typography>}
            sx={{flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column'}}
        >
            {isLoading && <Skeleton variant="rectangular" height={CHART_HEIGHT} />}

            {!isLoading && !trend && (
                <Typography variant="body2" color="text.secondary">No spend data available yet.</Typography>
            )}



            {trend && totals && (
                <Stack sx={{flex: 1, minHeight: 0}} gap={1}>
                    <Stack direction="row" gap={2} flexWrap="wrap">
                        <Stack direction="row" gap={1} alignItems="center">
                            <Box sx={{width: 10, height: 10, backgroundColor: COLORS.info.main, borderRadius: '2px'}}></Box>
                            <Typography variant="caption" color="grey.700" sx={{height: '16px'}}>
                                {`${trend.curr_label} · `}
                            </Typography>
                            <Typography component="span" variant="caption" fontWeight={700} color="text.primary" sx={{height: '16px'}}>
                                <CurrencyComponent v={totals.curr} />
                            </Typography>
                        </Stack>
                        <Stack direction="row" gap={1} alignItems="center">
                            <Box sx={{width: 10, height: 10, backgroundColor: COLORS.grey[400], borderRadius: '2px'}}></Box>
                            <Typography variant="caption" color="grey.700" sx={{height: '16px'}}>
                                {`${trend.prev_label} · `}
                            </Typography>
                            <Typography component="span" variant="caption" fontWeight={700} color="text.primary" sx={{height: '16px'}}>
                                <CurrencyComponent v={totals.prev} />
                            </Typography>
                        </Stack>
                        {yoyPct !== null ? (
                            <Typography
                                fontSize={10}
                                color={yoyPct >= 0 ? 'success.main' : 'error.main'}
                                sx={{backgroundColor: yoyPct >= 0 ? 'success.light' : 'error.light', padding: '4px 8px', borderRadius: '4px'}}
                                title={trend.yoy_tooltip}
                            >
                                {`${yoyPct >= 0 ? '+' : '-'}${Math.abs(yoyPct)}% YoY`}
                            </Typography>
                        ) : undefined}
                    </Stack>

                    <Box sx={{flex: 1, width: '100%', minHeight: CHART_HEIGHT, '& > div': {height: '100%'}}}>
                        <MithraEchart
                            initChart={(echart, root) => initTrendChart(echart, root, trend, p.currencySymbol)}
                            updateChart={(chart, data) => {
                                const next = data[0] as SupplierSpendTrend | undefined;
                                if (next) chart.setOption(buildTrendOption(next, p.currencySymbol), {notMerge: true});
                            }}
                            data={chartData}
                        />
                    </Box>
                </Stack>
            )}
        </CustomCard>
    );
};
